import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, User, LogOut, Package, ChevronDown } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import CartDrawer from './CartDrawer';

const TubelightNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState('/');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const { getCartCount, isCartOpen, setIsCartOpen } = useCart();
  const { currentUser, logout } = useAuth();
  const cartCount = getCartCount();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  React.useEffect(() => {
    if (location.pathname === '/' || location.pathname.startsWith('/product')) {
      setActiveTab('/');
    } else if (location.pathname === '/products') {
      setActiveTab('/products');
    }
  }, [location]);

  const tabs = [
    { id: '/', label: 'Home', path: '/' },
    { id: '/products', label: 'Products', path: '/products' },
  ];

  const handleTabClick = (path) => {
    setActiveTab(path);
    navigate(path);
  };

  return (
    <>
      <CartDrawer />
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-brand-navy/95 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div 
            className="text-lg sm:text-xl font-bold tracking-tight cursor-pointer text-white" 
            onClick={() => handleTabClick('/')} 
            data-testid="brand-logo"
          >
            BCStore
          </div>
          
          <div className="relative flex items-center space-x-1 bg-white/10 rounded-full p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.path)}
                className={`relative px-4 sm:px-6 py-1.5 sm:py-2 text-xs sm:text-sm font-medium transition-colors duration-200 rounded-full z-10 ${
                  activeTab === tab.path ? 'text-white' : 'text-gray-300 hover:text-white'
                }`}
                data-testid={`nav-${tab.label.toLowerCase()}`}
              >
                {activeTab === tab.path && (
                  <motion.div
                    layoutId="tubelight"
                    className="absolute inset-0 bg-brand-blue rounded-full"
                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <span className="relative z-10">{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button 
              onClick={() => setIsCartOpen(true)}
              className="relative p-2 hover:bg-white/10 rounded-full transition-colors text-white"
              data-testid="cart-button"
            >
              <ShoppingBag size={20} className="sm:w-5 sm:h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-brand-blue text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-bold">
                  {cartCount}
                </span>
              )}
            </button>

            {currentUser ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 p-2 hover:bg-white/10 rounded-full transition-colors text-white"
                >
                  <User size={20} />
                  <ChevronDown size={16} className={`transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {showUserMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                    >
                      <div className="px-4 py-2 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {currentUser.email}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          navigate('/profile');
                          setShowUserMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <User size={16} />
                        My Profile
                      </button>
                      <button
                        onClick={() => {
                          navigate('/my-orders');
                          setShowUserMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Package size={16} />
                        My Orders
                      </button>
                      <div className="border-t border-gray-200 my-1"></div>
                      <button
                        onClick={async () => {
                          try {
                            await logout();
                            setShowUserMenu(false);
                            navigate('/');
                          } catch (error) {
                            console.error('Logout error:', error);
                          }
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <LogOut size={16} />
                        Logout
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="px-4 py-2 bg-brand-blue hover:bg-white hover:text-brand-navy text-white text-sm font-medium rounded-full transition-colors"
              >
                Login
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
    </>
  );
};

export default TubelightNavbar;